import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxImportSource react */
import { useMemo } from "react";
import { Box, Text } from "ink";
import { buildNamespaceTree, flattenTree, } from "../../NamespaceTree.js";
import { formatModeNote } from "../../ModeTag.js";
export function Plan({ plan }) {
    const items = useMemo(() => [
        ...Object.values(plan.resources),
        ...Object.values(plan.deletions),
    ], [plan]);
    const taskItems = useMemo(() => [
        ...Object.values(plan.actions ?? {}),
        ...Object.values(plan.actionDeletions ?? {}),
    ].filter((t) => t !== undefined), [plan]);
    const flatItems = useMemo(() => {
        const tree = buildNamespaceTree(items, taskItems);
        return flattenTree(tree);
    }, [items, taskItems]);
    if (items.length === 0 && taskItems.length === 0) {
        return _jsx(Text, { color: "gray", children: "No changes planned" });
    }
    const counts = items.reduce((acc, item) => (acc[item.action]++, acc), {
        create: 0,
        update: 0,
        delete: 0,
        noop: 0,
        replace: 0,
    });
    const taskCounts = taskItems.reduce((acc, item) => (acc[item.action]++, acc), { run: 0, noop: 0, delete: 0 });
    const actions = ["create", "update", "delete", "replace"].filter((action) => counts[action] > 0);
    const taskHeaderEntries = [
        taskCounts.run > 0 ? `${taskCounts.run} to run` : undefined,
        taskCounts.delete > 0 ? `${taskCounts.delete} to drop` : undefined,
    ].filter((s) => !!s);
    return (_jsxs(Box, { flexDirection: "column", children: [_jsxs(Box, { marginTop: 1, children: [_jsx(Text, { underline: true, children: "Plan" }), _jsx(Text, { children: ": " }), [
                        ...actions.map((action) => {
                            const count = counts[action];
                            const color = actionColor(action);
                            return (_jsx(Box, { children: _jsxs(Text, { color: color, children: [count, " to ", action] }) }, action));
                        }),
                        ...taskHeaderEntries.map((label, i) => (_jsx(Box, { children: _jsx(Text, { color: "cyan", children: label }) }, `task-${i}`))),
                    ].flatMap((box, i, arr) => i === arr.length - 1
                        ? [box]
                        : [box, _jsx(Text, { children: " | " }, `sep-${i}`)])] }), _jsx(Box, { flexDirection: "column", marginTop: 1, children: flatItems.map((item) => {
                    const indent = "  ".repeat(item.depth);
                    const color = getActionColor(item.action);
                    const icon = getActionIcon(item.action);
                    const key = item.path.join("/");
                    if (item.type === "namespace") {
                        return (_jsxs(Box, { flexDirection: "row", children: [_jsx(Text, { children: indent }), _jsx(Box, { width: 2, children: _jsxs(Text, { color: color, children: [icon, " "] }) }), _jsx(Text, { color: "blueBright", children: item.id })] }, key));
                    }
                    if (item.type === "binding") {
                        return (_jsxs(Box, { flexDirection: "row", children: [_jsx(Text, { children: indent }), _jsx(Box, { width: 2, children: _jsxs(Text, { color: color, children: [icon, " "] }) }), _jsx(Text, { color: "cyan", children: item.bindingSid })] }, key));
                    }
                    if (item.type === "action") {
                        return (_jsxs(Box, { flexDirection: "row", children: [_jsx(Text, { children: indent }), _jsx(Box, { width: 2, children: _jsxs(Text, { color: color, children: [icon, " "] }) }), _jsx(Box, { children: _jsx(Text, { bold: true, children: item.id }) }), _jsx(Box, { marginLeft: 1, children: _jsxs(Text, { color: "blackBright", children: ["(", item.actionType, ")"] }) }), _jsx(Box, { marginLeft: 1, children: _jsx(Text, { color: "cyan", children: "[action]" }) })] }, key));
                    }
                    // Resource item
                    const modeNote = formatModeNote({
                        mode: item.providerMode,
                        priorMode: item.fromProviderMode,
                        defaultMode: plan.defaultMode,
                    });
                    return (_jsxs(Box, { flexDirection: "row", children: [_jsx(Text, { children: indent }), _jsx(Box, { width: 2, children: _jsxs(Text, { color: color, children: [icon, " "] }) }), _jsx(Box, { children: _jsx(Text, { bold: true, children: item.id }) }), _jsx(Box, { marginLeft: 1, children: _jsxs(Text, { color: "blackBright", children: ["(", item.resourceType, ")"] }) }), modeNote && (_jsx(Box, { marginLeft: 1, children: _jsxs(Text, { color: "blackBright", children: ["(", modeNote, ")"] }) })), item.bindingCount !== undefined && item.bindingCount > 0 && (_jsx(Box, { marginLeft: 1, children: _jsxs(Text, { color: "cyan", children: ["(", item.bindingCount, " bindings)"] }) }))] }, key));
                }) })] }));
}
const getActionColor = (action) => ({
    noop: "gray",
    create: "green",
    update: "yellow",
    delete: "red",
    replace: "magenta",
    mixed: "cyan",
    run: "cyan",
})[action] ?? "gray";
const getActionIcon = (action) => ({
    create: "+",
    update: "~",
    delete: "-",
    noop: "•",
    replace: "!",
    mixed: "*",
    run: "λ",
})[action] ?? "?";
const actionColor = (action) => getActionColor(action);
//# sourceMappingURL=Plan.js.map